/**
 * NpcTalkThreads — the dialogue PATHS of the talk window (2026-09-20).
 *
 * The Morrowind topic window answers; it never ASKS, never remembers what you
 * said, and nothing you learn from one versona matters to another. This helper
 * layers the seven path families of lib/dialogue_threads over it — the engine
 * is pure, so everything impure lives here:
 *
 *   OPENERS             after the greeting the versona may bring something up
 *                       (pickOpener — BEFORE noteVisit, the ORDER LAW)
 *   A QUESTION FOR YOU  reply chips under their line; changing the subject
 *                       leaves the question hanging, and that is remembered
 *   THEIR STORY         a life in chapters, one a day, the later ones on trust
 *   TRADE A TALE        the satchel: tell the tale their mood wants, get one back
 *   WHO TOLD YOU THAT?  memory_provenance → a lead at the teller
 *   ASK ABOUT: <lead>   shown only on the versona the trail points at
 *   WHERE DO YOU STAND? opinions; the tone of a reply lands by personality
 *
 * Memory (`TalkMemory`) is the player's own notebook: localStorage, versioned,
 * capped. What must be TRUE for everyone — a versona remembering what you told
 * it, the day's goodwill, a nudged political lean — goes through ONE reducer,
 * `converseWithVersona`, as closed-vocabulary enums (never text).
 *
 * Not an IModule: NpcInteractModule owns the window and hands this a `TalkHost`.
 */
import { atlas } from '../state.js';
import { makeWindow, type UiWindow } from '../lib/ui_window.js';
import { nameFromTables } from '../lib/versona_names.js';
import { titleOf } from '../lib/professions.js';
import { effectiveLeanX10 } from '../lib/opinion_lean.js';
import type { TopicFacts } from '../lib/topic_dialogue.js';
import { THREADS } from '../lib/dialogue_paths.js';
import {
  CHAPTER_REPLIES, PERSONA_VALUES,
  addTale, answerLead, applyEffects, beatOf, canSwapTale, emptyTalkMemory, fillThread, judgeTale, landingLine,
  leadsFor, nextChapter, noteTaleTold, noteThread, noteVisit, openLead, personaWord, pickOpener, reviveTalkMemory,
  sourceLine, taleAskLine, taleFromVersona, talkOf, tierRank, toneLanding, verdictEarns, verdictLine,
  type Lead, type PersonaSlot, type Reply, type RumorFact, type TalkMemory, type Tale, type ThreadDef, type ThreadFacts,
  type WitnessFact,
} from '../lib/dialogue_threads.js';

export interface TalkHost {
  tbl<T>(name: string): Iterable<T>;
  /** A versona paragraph — linkified against the current facts; `spoken` false = rendered, not voiced. */
  npcSay(text: string, spoken?: boolean): void;
  playerSay(text: string): void;
  system(text: string): void;
  logEl(): HTMLDivElement | null;
  discover(topicKey: string): void;
  /** Re-render the topic column + header (memory changed what is askable). */
  refresh(): void;
  /** Settlement id → its display name ('' unknown). */
  townName(settlementId: bigint): string;
}

interface AgentLite { versonaId: bigint; settlementId: bigint; job: string; hunger: number; wallet: bigint; boldness: number; greed: number; diligence: number; sociability: number }
interface MemoryLite { memoryId: bigint; versonaId: bigint; kind: string; factKey: string; text: string; importance: number }
interface ProvLite { memoryId: bigint; versonaId: bigint; tellerId: bigint; originId: bigint; hops: number; confidence: number }
interface OpinionLite { versonaId: bigint; leanX10: number; updatedAt: { microsSinceUnixEpoch: bigint } }
interface OriginLite { versonaId: bigint; settlementId: bigint }
interface SkillLite { versonaId: bigint; recipeKey: string; level: number }

const STORE_KEY = 'atlas:talk-memory';
const CHIP_CSS = 'text-align:left;padding:4px 9px;background:var(--ax-bg3);color:var(--ax-text);border:1px solid var(--ax-line2);border-radius:var(--ax-win-radius);cursor:pointer;font:12px/1.35 var(--ax-font)';
const ACT_CSS = 'text-align:left;padding:3px 6px;background:none;border:0;border-radius:var(--ax-radius);cursor:pointer;font:12px var(--ax-font);color:var(--ax-rare)';
const MOOD_LABEL: Record<string, string> = { hopeful: 'hopeful', brave: 'brave', funny: 'funny', sad: 'sad', strange: 'strange', justice: 'justice' };
const SLOT_LABEL: Record<PersonaSlot, string> = { origin: 'Where you are from', trade: 'What you do', purpose: 'Why you came', warView: 'On the war', rivalView: 'On the other civilization', townView: 'On their town' };

export class NpcTalkThreads {
  private mem: TalkMemory = emptyTalkMemory();
  private loadedFor = '';
  private chips: HTMLDivElement | null = null;
  private current: bigint | null = null;
  private lastOpener = '';
  private journal: UiWindow | null = null;
  private remembered = 0;

  /** The probe lever THIS instance registered (dispose removes it only while it is still ours). */
  private readonly lever: unknown;

  constructor(private readonly host: TalkHost) {
    this.lever = (window as unknown as { __talkThreads?: unknown }).__talkThreads = Object.assign(
      () => ({
        versona: this.current?.toString() ?? null,
        chips: this.chips ? [...this.chips.querySelectorAll('button')].map((b) => b.textContent ?? '') : [],
        lastOpener: this.lastOpener,
        remembered: this.remembered,
        persona: { ...this.memory().persona },
        tales: this.memory().tales.length,
        leads: this.memory().leads.map((l) => ({ text: l.text, target: l.targetName, state: l.state, trail: l.trail })),
        visits: this.current !== null ? talkOf(this.memory(), this.current.toString()).visits : 0,
      }),
      { reset: (): void => { this.mem = emptyTalkMemory(); this.save(); }, journal: (): void => this.openJournal() },
    );
  }

  // ── memory (the player's notebook — per identity, versioned, capped by the engine) ──
  private storeKey(): string { const hex = atlas.identityHex; return hex ? `${STORE_KEY}:${hex.slice(0, 12)}` : STORE_KEY; }
  private memory(): TalkMemory {
    const key = this.storeKey();
    if (this.loadedFor !== key) {
      this.loadedFor = key;
      try { this.mem = reviveTalkMemory(JSON.parse(localStorage.getItem(key) ?? 'null')); } catch { this.mem = emptyTalkMemory(); }
    }
    return this.mem;
  }
  private save(): void {
    try { localStorage.setItem(this.storeKey(), JSON.stringify(this.mem)); } catch { /* private mode — the notebook lasts the session */ }
  }

  // ── facts: everything the engine may know about THIS versona, from the replicated rows ──
  facts(base: TopicFacts, versonaId: bigint): ThreadFacts {
    const conn = atlas.conn;
    let agent: AgentLite | null = null;
    const agentOf = new Map<string, AgentLite>();
    for (const a of this.host.tbl<AgentLite>('npcAgent')) { agentOf.set(a.versonaId.toString(), a); if (a.versonaId === versonaId) agent = a; }
    const sid = agent?.settlementId ?? 0n;
    let opinion: { leanX10: number; updatedAtMicros: bigint } | null = null;
    for (const o of this.host.tbl<OpinionLite>('versonaOpinion')) if (o.versonaId === versonaId) { opinion = { leanX10: o.leanX10, updatedAtMicros: o.updatedAt.microsSinceUnixEpoch }; break; }
    // The EFFECTIVE lean, computed as the server's leanOf does — so the side they argue here is the side the
    // server nudges from when you disagree (lib/opinion_lean, the mirror).
    const leanX10 = agent ? effectiveLeanX10(agent, agent.wallet, agent.hunger, opinion, BigInt(Date.now()) * 1000n) : null;
    let bornTown = '', migrant = false;
    for (const o of this.host.tbl<OriginLite>('versonaOrigin')) {
      if (o.versonaId !== versonaId) continue;
      bornTown = this.host.townName(o.settlementId);
      migrant = o.settlementId !== 0n && sid !== 0n && o.settlementId !== sid;
      break;
    }
    const skills: SkillLite[] = [];
    for (const s of this.host.tbl<SkillLite>('npcSkill')) if (s.versonaId === versonaId) skills.push(s);
    const plainJob = (agent?.job ?? '').replace(/_/g, ' ');
    const earned = agent ? titleOf(agent.job, skills) : '';
    const prov = new Map<string, ProvLite>();
    for (const p of this.host.tbl<ProvLite>('memoryProvenance')) if (p.versonaId === versonaId) prov.set(p.memoryId.toString(), p);
    const rumorRows: MemoryLite[] = [];
    const witnessFacts: WitnessFact[] = [];
    for (const m of this.host.tbl<MemoryLite>('versonaMemory')) {
      if (m.versonaId !== versonaId) continue;
      if (m.kind === 'rumor') rumorRows.push(m);
      // What YOU confided is theirs to keep, not a tale to hand back to you.
      else if (m.kind === 'witness' && !m.factKey.startsWith('told:')) witnessFacts.push({ memoryId: m.memoryId.toString(), text: m.text, factKey: m.factKey, importance: m.importance });
    }
    // Newest first — the SAME order buildFacts uses for `rumors`, so %rumor% and rumorFacts[0] are one rumor.
    rumorRows.sort((a, b) => (a.memoryId > b.memoryId ? -1 : 1));
    const rumorFacts: RumorFact[] = rumorRows.map((m) => {
      const p = prov.get(m.memoryId.toString());
      const tellerId = p?.tellerId ?? 0n;
      const teller = tellerId !== 0n ? agentOf.get(tellerId.toString()) : undefined;
      return {
        memoryId: m.memoryId.toString(), text: m.text, factKey: m.factKey,
        tellerId: tellerId.toString(),
        // A teller who has died leaves no agent row: the name still resolves (origin outlives them), the town does not.
        tellerName: tellerId !== 0n ? nameFromTables(conn, tellerId) : '',
        tellerTown: teller ? this.host.townName(teller.settlementId) : '',
        hops: p?.hops ?? 1, confidence: p?.confidence ?? 55,
        planted: p ? p.tellerId === 0n && p.originId === 0n : true,
      };
    });
    return {
      ...base,
      versonaId: versonaId.toString(), settlementId: sid.toString(),
      boldness: agent?.boldness ?? 50, greed: agent?.greed ?? 50, diligence: agent?.diligence ?? 50, sociability: agent?.sociability ?? 50,
      leanX10, bornTown, migrant,
      title: earned !== plainJob ? earned : '',
      rumorFacts, witnessFacts,
      nowDay: Math.floor(Date.now() / 86_400_000),
    };
  }

  // ── the conversation's lifecycle ──
  /**
   * A conversation opens. Returns the opener's line ('' none) ALREADY rendered but NOT voiced — the caller
   * speaks greeting + opener as ONE utterance (a second speak() would cut the greeting off).
   */
  begin(tf: ThreadFacts, versonaId: bigint): string {
    this.close();
    this.current = versonaId;
    let mem = this.memory();
    const th = pickOpener(THREADS, tf, mem); // ORDER LAW: before noteVisit — `visits` must describe the PAST
    mem = noteVisit(mem, tf);
    let line = '';
    if (th) {
      mem = noteThread(mem, tf, th);
      this.mem = mem;
      this.lastOpener = th.id;
      line = this.playBeat(th, 'start', tf, '', false);
    } else {
      this.mem = mem;
      this.lastOpener = '';
    }
    this.save();
    return line;
  }

  /** A new partner takes the window NOW (2026-09-23): begin() may wait up to 1.5 s for a first meeting's rows, and
   *  meanwhile the topic column's story / tale / lead actions were live — a converse() in that gap credited the
   *  PREVIOUS partner (or, after Goodbye, was dropped). begin() re-binds the same id. */
  bind(versonaId: bigint): void {
    this.close();
    this.current = versonaId;
  }

  /** The window closed, or another partner took it. */
  close(): void {
    this.dismiss();
    this.current = null;
  }

  /**
   * The player changed the subject. A question left hanging stays ASKED-and-unanswered in memory
   * (Firewatch: silence is an answer too) — the bank calls it back another day.
   */
  dismiss(): void {
    this.chips?.remove();
    this.chips = null;
  }

  /** A topic answer carried their newest rumor: it goes into the satchel as hearsay. */
  heardRumor(tf: ThreadFacts): void {
    const r = tf.rumorFacts[0];
    if (!r) return;
    const before = this.memory().tales.length;
    this.mem = addTale(this.memory(), { text: r.text, from: tf.name, fromId: tf.versonaId, fromTown: tf.town, day: tf.nowDay, firstHand: false });
    if (this.mem.tales.length > before) { this.save(); this.host.system('(a tale for your satchel — see the Journal)'); }
  }

  // ── beats + reply chips ──
  private playBeat(th: ThreadDef, beatId: string, tf: ThreadFacts, prefix: string, spoken = true): string {
    const beat = beatOf(th, beatId);
    if (!beat) return '';
    const text = `${prefix}${fillThread(beat.say, tf, this.memory())}`.trim();
    this.host.npcSay(text, spoken);
    if (beat.say.includes('%rumor%')) this.heardRumor(tf);
    if (beat.replies && beat.replies.length > 0) this.showChips(beat.replies, (r) => this.onReply(th, r));
    return text;
  }

  private showChips(replies: readonly Reply[], onPick: (r: Reply) => void): void {
    this.dismiss();
    const log = this.host.logEl();
    if (!log) return;
    const box = document.createElement('div');
    box.dataset.threadChips = '1';
    box.style.cssText = 'align-self:flex-end;display:flex;flex-direction:column;gap:4px;max-width:92%;margin:2px 0';
    for (const r of replies) {
      const b = document.createElement('button');
      b.textContent = r.label;
      b.style.cssText = CHIP_CSS;
      b.onmouseenter = () => { b.style.background = 'var(--ax-sel)'; };
      b.onmouseleave = () => { b.style.background = 'var(--ax-bg3)'; };
      b.onclick = () => { if (this.chips !== box) return; this.dismiss(); onPick(r); };
      box.appendChild(b);
    }
    log.appendChild(box);
    log.scrollTop = log.scrollHeight;
    this.chips = box;
  }

  /** The current partner's facts, rebuilt — a reply may land seconds after the line it answers. */
  private factsNow: (() => ThreadFacts | null) = () => null;
  bindFacts(fn: () => ThreadFacts | null): void { this.factsNow = fn; }

  private onReply(th: ThreadDef, r: Reply): void {
    const tf = this.factsNow();
    if (!tf) return;
    this.host.playerSay(r.label);
    const res = applyEffects(this.memory(), tf, r.effects);
    this.mem = res.mem;
    let sourced = '';
    for (const e of r.effects ?? []) {
      if (e.t === 'persona' && PERSONA_VALUES[e.slot].includes(e.value)) this.converse('confide', e.slot, e.value);
      else if (e.t === 'opine' && e.stance !== 'dodge') this.converse('opine', e.issue, e.stance);
      else if (e.t === 'discover') this.host.discover(e.topic);
      else if (e.t === 'source') sourced = this.answerSource(tf);
    }
    // HOW you said it lands by who THEY are. A neutral landing is silence unless nothing else answers the reply.
    const landing = toneLanding(r.tone, tf);
    const lead = landing !== 'neutral' ? `${landingLine(r.tone, tf)} ` : '';
    if (r.goto) this.playBeat(th, r.goto, tf, lead);
    else if (sourced !== '') this.host.npcSay(`${lead}${sourced}`.trim());
    else if (r.tone) this.host.npcSay(landingLine(r.tone, tf));
    if (res.remembered) { this.remembered++; this.host.system(`— ${tf.name} will remember that.`); }
    this.save();
    this.host.refresh();
  }

  /** "Who told you that?" — their newest rumor's provenance, and a lead at the teller when there is one. */
  private answerSource(tf: ThreadFacts): string {
    const r = tf.rumorFacts[0];
    if (!r) return 'Did I say that? It\'s gone clean out of my head.';
    const opened = openLead(this.memory(), r, tf);
    this.mem = opened.mem;
    if (opened.lead && opened.lead.state === 'open') {
      const where = opened.lead.targetTown && opened.lead.targetTown !== tf.town ? ` in ${opened.lead.targetTown}` : '';
      this.host.system(`(a lead: ask ${opened.lead.targetName}${where} about it — it is in your Journal)`);
    }
    return sourceLine(r, tf);
  }

  // ── the topic column's path actions ──
  /** Their story · Trade a tale · Ask about: <lead> · Journal — appended by the host under the topic list. */
  renderActions(pane: HTMLElement, tf: ThreadFacts): void {
    const civil = tf.relTier !== 'hostile' && tf.relTier !== 'nemesis';
    const add = (label: string, title: string, onClick: (b: HTMLButtonElement) => void, css = ACT_CSS): HTMLButtonElement => {
      const b = document.createElement('button');
      b.textContent = label;
      b.title = title;
      b.style.cssText = css;
      b.onmouseenter = () => { b.style.background = 'var(--ax-bg3)'; };
      b.onmouseleave = () => { b.style.background = 'none'; };
      b.onclick = () => onClick(b);
      pane.appendChild(b);
      return b;
    };
    if (civil) {
      // THE WINDOW (Heaven's Vault): a lead is askable only on the versona its trail points at.
      for (const lead of leadsFor(tf, this.memory())) {
        const b = add(`Ask about: ${lead.text.slice(0, 40)}${lead.text.length > 40 ? '…' : ''}`, `A rumor you are tracing to its source — ${lead.trail.join(' → ')} → ${tf.name}`, () => this.askLead(lead));
        b.dataset.path = 'lead';
        b.style.color = 'var(--ax-hi)';
      }
      add('Their story', 'Ask about their life — one chapter a day; the later ones are kept for friends.', () => this.askStory()).dataset.path = 'story';
      add('Trade a tale…', 'Tell them a tale from your satchel. The right one for their mood earns one back. One a day.', (b) => this.toggleTalePicker(b)).dataset.path = 'tale';
    }
    add('Journal…', 'Leads you are tracing, the tales you carry, and what you have told people about yourself.', () => this.openJournal(), `${ACT_CSS};color:var(--ax-soft)`).dataset.path = 'journal';
  }

  private askStory(): void {
    const tf = this.factsNow();
    if (!tf) return;
    this.dismiss();
    this.host.playerSay('Tell me about yourself.');
    const res = nextChapter(tf, this.memory());
    this.host.npcSay(res.say);
    if (res.kind !== 'tell') return;
    const chapterKey = res.chapter.key;
    this.showChips(CHAPTER_REPLIES, (r) => {
      const now = this.factsNow();
      if (!now) return;
      this.host.playerSay(r.label);
      this.mem = applyEffects(this.memory(), now, r.effects).mem;
      // The thing they SAW is a first-hand tale, and you were told it to your face.
      if (chapterKey === 'hard') {
        const hard = [...now.witnessFacts].sort((a, b) => b.importance - a.importance)[0];
        if (hard) {
          const before = this.mem.tales.length;
          this.mem = addTale(this.mem, { text: hard.text, from: now.name, fromId: now.versonaId, fromTown: now.town, day: now.nowDay, firstHand: true });
          if (this.mem.tales.length > before) this.host.system('(a first-hand tale for your satchel)');
        }
      }
      this.host.npcSay(landingLine(r.tone, now));
      this.converse('heard', '', '');
      this.save();
      this.host.refresh();
    });
  }

  private askLead(lead: Lead): void {
    const tf = this.factsNow();
    if (!tf) return;
    this.dismiss();
    this.host.playerSay(`I'm told you're the one to ask about this: "${lead.text}"`);
    const res = answerLead(this.memory(), lead, tf);
    this.mem = res.mem;
    this.host.npcSay(res.line);
    if (res.outcome === 'resolved') {
      this.host.system('— you found the witness. The tale as it HAPPENED is in your satchel.');
      this.converse('traced', '', '');
    } else if (res.outcome === 'advanced') {
      const next = this.mem.leads.find((l) => l.id === lead.id);
      if (next) this.host.system(`(the trail moves on: ask ${next.targetName}${next.targetTown ? ` in ${next.targetTown}` : ''})`);
    } else {
      this.host.system('(the trail has gone cold)');
    }
    this.save();
    this.host.refresh();
  }

  private toggleTalePicker(anchor: HTMLButtonElement): void {
    const pane = anchor.parentElement;
    const prev = pane?.querySelector('.tale-swap');
    if (prev) { prev.remove(); return; }
    const tf = this.factsNow();
    if (!tf) return;
    this.dismiss();
    if (!canSwapTale(tf, this.memory())) {
      this.host.npcSay(tierRank(tf.relTier) < tierRank('cold') ? 'I\'ve no ear for your stories.' : 'One tale a day, friend. Let me sit with the last one.');
      return;
    }
    const tales = [...this.memory().tales].reverse();
    if (tales.length === 0) {
      this.host.system('(your satchel is empty — listen for the latest word, ask who told it, hear people\'s stories)');
      return;
    }
    this.host.playerSay('I carry a tale or two. Would you hear one?');
    this.host.npcSay(taleAskLine(tf));
    const box = document.createElement('div');
    box.className = 'tale-swap';
    box.style.cssText = 'display:flex;flex-direction:column;margin:2px 0 2px 14px;border-left:1px solid var(--ax-line2);padding-left:6px;max-height:140px;overflow:auto;scrollbar-width:thin';
    for (const tale of tales.slice(0, 10)) {
      const opt = document.createElement('button');
      opt.textContent = `${tale.firstHand ? '★ ' : ''}[${MOOD_LABEL[tale.mood] ?? tale.mood}] ${tale.text.slice(0, 44)}${tale.text.length > 44 ? '…' : ''}`;
      opt.title = `${tale.text}\n— from ${tale.from}${tale.fromTown ? ` of ${tale.fromTown}` : ''}${tale.firstHand ? ' (first-hand)' : ''}`;
      opt.style.cssText = ACT_CSS;
      opt.onclick = () => { box.remove(); this.tellTale(tale); };
      box.appendChild(opt);
    }
    anchor.after(box);
  }

  private tellTale(tale: Tale): void {
    const tf = this.factsNow();
    if (!tf) return;
    this.host.playerSay(`"${tale.text}"`);
    const verdict = judgeTale(tale, tf);
    let line = verdictLine(verdict, tf);
    this.mem = noteTaleTold(this.memory(), tale.id, tf);
    if (verdictEarns(verdict)) {
      this.converse('tale', verdict, '');
      // A good telling earns one back: the best thing they saw, else the freshest thing they heard.
      const back = taleFromVersona(tf, this.mem);
      if (back) {
        const before = this.mem.tales.length;
        this.mem = addTale(this.mem, { text: back.text, from: tf.name, fromId: tf.versonaId, fromTown: tf.town, day: tf.nowDay, firstHand: back.firstHand });
        if (this.mem.tales.length > before) {
          line += back.firstHand ? ` Here's one for yours — and this I SAW: ${back.text}` : ` Here's one for yours, as I heard it: ${back.text}`;
          this.host.npcSay(line);
          this.host.system('(a tale for your satchel)');
          this.save();
          this.host.refresh();
          return;
        }
      }
    }
    this.host.npcSay(line);
    this.save();
    this.host.refresh();
  }

  // ── the one server call: closed vocabulary, never text ──
  private converse(act: 'confide' | 'opine' | 'tale' | 'traced' | 'heard', slot: string, value: string): void {
    if (this.current === null) return;
    const r = atlas.conn?.reducers as unknown as { converseWithVersona?(a: { versonaId: bigint; act: string; slot: string; value: string }): Promise<void> } | undefined;
    try { void r?.converseWithVersona?.({ versonaId: this.current, act, slot, value })?.catch((e: Error) => this.host.system(`(${e.message})`)); } catch { /* pre-publish server — the notebook still works */ }
  }

  // ── the Journal ──
  openJournal(): void {
    if (!this.journal) this.journal = makeWindow({ id: 'talk-journal', title: 'Traveler\'s Journal', width: 400, height: 460, minWidth: 320, minHeight: 260 });
    this.renderJournal();
    this.journal.setOpen(true);
  }

  private renderJournal(): void {
    const body = this.journal?.body;
    if (!body) return;
    body.textContent = '';
    const p = document.createElement('div');
    p.id = 'talk-journal-body';
    p.style.cssText = 'padding:10px 12px;color:var(--ax-text);font:12px/1.5 var(--ax-font);overflow:auto;height:100%;box-sizing:border-box;scrollbar-width:thin';
    body.appendChild(p);
    const mem = this.memory();
    const head = (text: string, note: string): void => {
      const h = document.createElement('div');
      h.textContent = text;
      h.style.cssText = 'color:var(--ax-muted);font:600 10px var(--ax-font);letter-spacing:1px;margin:10px 0 2px';
      p.appendChild(h);
      if (note) { const n = document.createElement('div'); n.textContent = note; n.style.cssText = 'color:var(--ax-muted);font-style:italic;margin-bottom:4px'; p.appendChild(n); }
    };
    const row = (html: Array<[string, string]>): void => {
      const d = document.createElement('div');
      d.style.cssText = 'padding:4px 0;border-bottom:1px solid var(--ax-line)';
      for (const [text, css] of html) { const s = document.createElement('span'); s.textContent = text; s.style.cssText = css; d.appendChild(s); }
      p.appendChild(d);
    };
    // LEADS — open first.
    head('LEADS', mem.leads.length === 0 ? 'When someone tells you a rumor, ask who told THEM. The trail leads somewhere.' : '');
    const order: Record<string, number> = { open: 0, resolved: 1, cold: 2 };
    for (const l of [...mem.leads].sort((a, b) => order[a.state] - order[b.state])) {
      const tag = l.state === 'open' ? `ASK ${l.targetName}${l.targetTown ? ` · ${l.targetTown}` : ''}` : l.state === 'resolved' ? 'FOUND THE WITNESS' : 'GONE COLD';
      row([[`${tag}  `, `font:600 10px var(--ax-font);color:${l.state === 'open' ? 'var(--ax-hi)' : l.state === 'resolved' ? 'var(--ax-good)' : 'var(--ax-muted)'}`], [`“${l.text}”`, 'color:var(--ax-text)'], [`  ${l.trail.join(' → ')}`, 'color:var(--ax-muted)']]);
    }
    // THE SATCHEL.
    head('TALE SATCHEL', mem.tales.length === 0 ? 'Tales you have been told. The right tale for the right mood earns one back.' : '★ = first-hand, from someone who was there');
    for (const tl of [...mem.tales].reverse()) {
      row([[`${tl.firstHand ? '★ ' : ''}[${MOOD_LABEL[tl.mood] ?? tl.mood}] `, 'font:600 10px var(--ax-font);color:var(--ax-rare)'], [tl.text, 'color:var(--ax-text)'], [`  — ${tl.from}${tl.fromTown ? `, ${tl.fromTown}` : ''}${tl.toldTo.length > 0 ? ` · told to ${tl.toldTo.length}` : ''}`, 'color:var(--ax-muted)']]);
    }
    // WHAT YOU HAVE SAID.
    const slots = Object.keys(mem.persona) as PersonaSlot[];
    head('WHAT YOU HAVE SAID OF YOURSELF', slots.length === 0 ? 'People will ask. What you answer is remembered — and it gets around.' : '');
    for (const s of slots) row([[`${SLOT_LABEL[s]}: `, 'color:var(--ax-soft)'], [personaWord(s, mem.persona[s] ?? ''), 'color:var(--ax-text)']]);
    for (const [sid, town] of Object.entries(mem.toldInTown)) {
      const name = this.host.townName(BigInt(sid)) || 'a town';
      for (const s of Object.keys(town) as PersonaSlot[]) {
        const rec = town[s];
        if (rec) row([[`In ${name} they say you are ${personaWord(s, rec.value)}`, 'color:var(--ax-text)'], [` — you told ${rec.byName}`, 'color:var(--ax-muted)']]);
      }
    }
  }

  dispose(): void {
    this.close();
    this.journal?.root.remove();
    this.journal = null;
    const w = window as { __talkThreads?: unknown };
    if (w.__talkThreads === this.lever) delete w.__talkThreads;
  }
}
